import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";

const REQUEST_TYPES = [
  { value: "expert", label: "Expert" },
  { value: "speaker", label: "Speaker" },
  { value: "product", label: "Product" },
  { value: "man_on_the_street", label: "Man on the Street" },
];

const REQUEST_SUB_TYPES = [
  { value: "online_print", label: "Online / Print" },
  { value: "video_broadcast", label: "Video / Broadcast" },
  { value: "podcast", label: "Podcast" },
  { value: "radio", label: "Radio" },
  { value: "conference", label: "Conference" },
  { value: "lecture", label: "Lecture" },
  { value: "webinar", label: "Webinar" },
  { value: "event", label: "Event" },
  { value: "shared_article", label: "Shared Article" },
  { value: "other", label: "Other" },
];

const SUB_TYPES_WITHOUT_LOGISTICS = new Set(["online_print", "shared_article", "other"]);

function RequestTypePicker({ show, onClose, onSave, currentType, currentSubType, hasLogisticsData }) {
  const [requestType, setRequestType] = useState(currentType);
  const [requestSubType, setRequestSubType] = useState(currentSubType || "online_print");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const willClearLogistics = hasLogisticsData && SUB_TYPES_WITHOUT_LOGISTICS.has(requestSubType);

  const handleSave = async () => {
    const fields = {};
    if (requestType !== currentType) fields.request_type = requestType;
    const newSub = requestSubType;
    if (newSub !== currentSubType) fields.request_sub_type = newSub;

    if (Object.keys(fields).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSave(fields);
      onClose();
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!saving) onClose();
      }}
    >
      <Modal.Header closeButton className="px-4 pt-4 pb-0 border-0">
        <Modal.Title className="paragraph-lg fw-bold">Edit Request Type</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-3">
        {error && <div className="alert alert-danger paragraph-sm mb-3">{error}</div>}

        <div className="mb-4">
          <h6 className="text-muted mb-2">Request Type</h6>
          {REQUEST_TYPES.map(({ value, label }) => (
            <div className="form-check" key={value}>
              <input
                className="form-check-input"
                type="radio"
                name="requestType"
                id={`rt-${value}`}
                value={value}
                checked={requestType === value}
                onChange={() => setRequestType(value)}
                disabled={saving}
              />
              <label className="form-check-label" htmlFor={`rt-${value}`}>
                {label}
              </label>
            </div>
          ))}
        </div>

        <div>
          <h6 className="text-muted mb-2">Sub Type</h6>
          {REQUEST_SUB_TYPES.map(({ value, label }) => (
            <div className="form-check" key={value}>
              <input
                className="form-check-input"
                type="radio"
                name="requestSubType"
                id={`rst-${value}`}
                value={value}
                checked={requestSubType === value}
                onChange={() => setRequestSubType(value)}
                disabled={saving}
              />
              <label className="form-check-label" htmlFor={`rst-${value}`}>
                {label}
              </label>
            </div>
          ))}
          {willClearLogistics && (
            <div className="text-warning small mt-2">
              <i className="fa fa-exclamation-triangle me-1" />
              Logistics details, equipment notes, and links will be cleared.
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="px-4 pb-4 pt-0 border-0">
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <i className="fa fa-spinner fa-spin me-1" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RequestTypePicker;
